export function getTime(seconds) {
  if (seconds < 60) {
    return `${seconds} s`;
  } else if (seconds < 3600) {
    return `${parseInt(seconds / 60)} m`;
  } else if (seconds < 86400) {
    return `${parseInt(seconds / 3600)} h`;
  } else {
    return `${parseInt(seconds / 86400)} d`;
  }
}

export const reasonsForResolutionList = [
  { label: "Simulacro", value: "exercise" },
  { label: "Exitoso", value: "resolved" },
  { label: "No encontrado", value: "false_alarm" },
]

export const fullReasonsForResolutionList = [
  { label: "Simulacro", value: "exercise" },
  { label: "Exitoso", value: "resolved" },
  { label: "No encontrado", value: "false_alarm" },
  { label: "No gestionada", value: "resolved_automatically" }
]

export const ALARM_LIMIT_IN_SEC = 900;

export function isOnline(date, minutes = 20) {
  const lastEventDate = new Date(date * 1000);
  const currentDate = new Date();
  const currentDateInSeconds = currentDate && Math.floor(currentDate.getTime() / 1000);
  const lastEventDateInSeconds = lastEventDate && Math.floor(lastEventDate.getTime() / 1000);
  const timeThreshold = minutes * 60;
  const sendingSignal = (currentDateInSeconds - lastEventDateInSeconds) < timeThreshold;

  return sendingSignal
}

export function getSpanishDate(date) {
  const d = new Date(date);
  const day = String(d.getDate()).padStart(2, '0');
  const monthNames = ["ENE", "FEB", "MAR", "ABR", "MAY", "JUN", "JUL", "AGO", "SEP", "OCT", "NOV", "DIC"];
  const month = monthNames[d.getMonth()];
  const year = String(d.getFullYear()).slice(-2);
  return `${day}/${month}/${year}`;
}

// ─── NEW v3 UTILITIES ───

/** Signal bars reference: 1 (weakest) → 6 (strongest) */
export const BARS = [
  { n: 6, open: "5m",    closed: "2.5m",  tag: "Muy cerca", c: "#ef4444" },
  { n: 5, open: "15m",   closed: "7m",    tag: "Cerca",     c: "#f97316" },
  { n: 4, open: "45m",   closed: "22m",   tag: "Medio",     c: "#eab308" },
  { n: 3, open: "100m",  closed: "50m",   tag: "Lejos",     c: "#22c55e" },
  { n: 2, open: "180m",  closed: "90m",   tag: "Muy lejos", c: "#3b82f6" },
  { n: 1, open: "+180m", closed: "+90m",  tag: "Límite",    c: "#8b5cf6" },
];

export const SITUATIONS_MAP = {
  "1": "Simulacro / Prueba de sistema",
  "2": "Guardia no asistió",
  "3": "Asistió, señal ya había cesado",
  "4": "Señal se perdió al acercarse",
  "5": "Señal persistente, sin localizar",
  "6": "Sospechoso identificado",
  "7": "Fuente conocida / ambiental",
  "8": "Sin gestión documentada / cierre automático"
};

export const R_LABELS = {
  drill:               "Simulacro",
  with_findings:       "Gestión con hallazgo",
  without_findings:    "Gestión sin hallazgo",
  unmanaged:           "Sin gestión"
};

/** Maps new closeResult values to colors */
export const R_COLORS = {
  drill:               "#a855f7", // Púrpura
  with_findings:       "#22c55e", // Verde
  without_findings:    "#64748b", // Gris
  unmanaged:           "#ef4444"  // Rojo
};

/**
 * Normalizes all possible legacy and V3 statuses into the 4 core categories.
 */
export function normalizeResult(r) {
  if (!r) return "";
  if (["with_findings", "resolved", "successful", "deterrence", "verified_unlocated", "out_of_norm"].includes(r)) return "with_findings";
  if (["without_findings", "not_found", "signal_not_found", "brief_unverifiable", "false_alarm"].includes(r)) return "without_findings";
  if (["unmanaged", "resolved_automatically"].includes(r)) return "unmanaged";
  if (["drill", "exercise"].includes(r)) return "drill";
  return r;
}

/**
 * Maps legacy reasonForResolution values to new closeResult keys
 * (for read-only display of old events).
 */
export const LEGACY_RESULT_MAP = {
  exercise:               "drill",
  resolved:               "with_findings",
  false_alarm:            "without_findings",
  resolved_automatically: "unmanaged",
};

/** Returns the display label for an alarm object (new or legacy). */
export function getResultLabel(alarm) {
  const r = normalizeResult(alarm.closeResult || LEGACY_RESULT_MAP[alarm.reasonForResolution] || alarm.reasonForResolution);
  return R_LABELS[r] || r || "—";
}

/** Returns the display color for an alarm object (new or legacy). */
export function getResultColor(alarm) {
  const r = normalizeResult(alarm.closeResult || LEGACY_RESULT_MAP[alarm.reasonForResolution] || alarm.reasonForResolution);
  return R_COLORS[r] || "#64748b";
}

/** Generate event ID in format CLFC-A083-001 */
export function genEventId(country, siteCode, date, seq) {
  const y = date.getFullYear();
  const yearLetter = String.fromCharCode(65 + (y - 2026)); // A=2026, B=2027…
  const dayOfYear = String(Math.floor((date - new Date(y, 0, 0)) / 86400000)).padStart(3, "0");
  const seqStr    = String(seq).padStart(3, "0");
  return `${country}${siteCode}-${yearLetter}${dayOfYear}-${seqStr}`;
}

/** Format millisecond timestamp → HH:MM */
export const fmtHM = ms =>
  ms ? new Date(ms).toLocaleTimeString("es-CL", { hour: "2-digit", minute: "2-digit" }) : "--";

/** Format millisecond timestamp → HH:MM:SS */
export const fmtHMS = ms =>
  ms ? new Date(ms).toLocaleTimeString("es-CL", { hour: "2-digit", minute: "2-digit", second: "2-digit" }) : "--";

/** Format millisecond timestamp → DD MMM */
export const fmtDate = ms =>
  ms ? new Date(ms).toLocaleDateString("es-CL", { day: "2-digit", month: "short" }) : "--";

/** Format seconds duration → "3m 12s" / "1h 5m" */
export function fmtDur(s) {
  if (!s && s !== 0) return "--";
  if (s < 60)   return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m ${s % 60}s`;
  return `${Math.floor(s / 3600)}h ${Math.floor((s % 3600) / 60)}m`;
}

/** Time-ago (for last ping): "12s" / "5min" / "2h" / "3d" */
export function ago(ms) {
  if (!ms) return "--";
  const d = Math.max(0, Date.now() - ms);
  if (d < 60000)    return `${Math.floor(d / 1000)}s`;
  if (d < 3600000)  return `${Math.floor(d / 60000)}min`;
  if (d < 86400000) return `${Math.floor(d / 3600000)}h`;
  return `${Math.floor(d / 86400000)}d`;
}

/** Utility to normalize Firestore Timestamps or raw millisecond numbers/objects safely into millisecond numbers */
export function getMillis(val) {
  if (!val) return 0;
  if (typeof val === "object" && typeof val.toMillis === "function") {
    return val.toMillis();
  }
  if (typeof val === "object" && typeof val.seconds === "number") {
    return val.seconds * 1000;
  }
  return Number(val);
}

/** True if timestamp is from today */
export const isToday = ms => ms && new Date(ms).toDateString() === new Date().toDateString();

/** True if timestamp is within the last 7 days */
export const isThisWeek = ms => ms && Math.max(0, Date.now() - ms) < 604800000;

